import React, { useEffect, useMemo, useState } from "react";
import { Modal, Select, Spin } from "antd";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  ApiDimension,
  BulkEntryCell,
  BulkEntryResponse,
  getBulkEntry,
  saveBulkEntry,
} from "./balancedScorecard.service";
import "./BulkEntryModal.css";

interface BulkEntryModalProps {
  open: boolean;
  scorecardId: string;
  dimensions: ApiDimension[];
  initialDimensionId: string | null;
  canManage: boolean;
  onClose: () => void;
  /** Called after a successful save so the parent can refresh counts. */
  onSaved: () => void;
}

// Max absolute value the API accepts (see the breakdown API doc).
const MAX_ABS = 999999.9999;
const NUMBER_RE = /^-?\d+(\.\d+)?$/;

const cellKey = (indicatorId: string, categoryId: string) =>
  `${indicatorId}:${categoryId}`;

/** "" → null (no data). Returns undefined if the text is not a valid number. */
const parseCell = (text: string): number | null | undefined => {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  if (!NUMBER_RE.test(trimmed)) return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || Math.abs(n) > MAX_ABS) return undefined;
  return n;
};

const BulkEntryModal: React.FC<BulkEntryModalProps> = ({
  open,
  scorecardId,
  dimensions,
  initialDimensionId,
  canManage,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation("balancedScorecard");
  const [dimensionId, setDimensionId] = useState<string | null>(null);
  const [grid, setGrid] = useState<BulkEntryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  // Text as the user typed it, keyed by indicator:category.
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset to the dimension the page passed in each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setDimensionId(initialDimensionId ?? dimensions[0]?.id ?? null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Load the grid whenever the dimension changes.
  useEffect(() => {
    if (!open || !dimensionId) return;
    let cancelled = false;
    setLoading(true);
    setGrid(null);
    getBulkEntry(scorecardId, dimensionId)
      .then((res) => {
        if (cancelled) return;
        setGrid(res);
        const initial: Record<string, string> = {};
        res.domains.forEach((d) =>
          d.indicators.forEach((ind) =>
            res.categories.forEach((c) => {
              const v = ind.values[c.id];
              initial[cellKey(ind.id, c.id)] = v == null ? "" : String(Number(v));
            }),
          ),
        );
        setDraft(initial);
      })
      .catch(() => {
        if (!cancelled) toast.error(t("bulkEntry.loadFailed"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dimensionId, scorecardId]);

  // Which cells differ from what the server has, plus which are not numbers.
  const { changes, invalidKeys, outOfRange } = useMemo(() => {
    const changes: BulkEntryCell[] = [];
    const invalidKeys = new Set<string>();
    let outOfRange = 0;
    if (!grid) return { changes, invalidKeys, outOfRange };

    grid.domains.forEach((d) =>
      d.indicators.forEach((ind) =>
        grid.categories.forEach((c) => {
          const key = cellKey(ind.id, c.id);
          const parsed = parseCell(draft[key] ?? "");
          if (parsed === undefined) {
            invalidKeys.add(key);
            return;
          }
          const rawOriginal = ind.values[c.id];
          const original = rawOriginal == null ? null : Number(rawOriginal);
          if (parsed !== original) {
            changes.push({
              indicator_id: ind.id,
              category_id: c.id,
              value: parsed,
            });
          }
          // Percentages are shown as a warning, not blocked (spec 6.3).
          if (
            ind.value_scale === "percentage" &&
            parsed !== null &&
            (parsed < 0 || parsed > 100)
          ) {
            outOfRange += 1;
          }
        }),
      ),
    );
    return { changes, invalidKeys, outOfRange };
  }, [grid, draft]);

  const dimension = dimensions.find((d) => d.id === dimensionId) ?? null;
  const categories = grid?.categories ?? [];
  const hasIndicators = (grid?.domains ?? []).some(
    (d) => d.indicators.length > 0,
  );

  const handleSave = async () => {
    if (!dimensionId) return;
    if (invalidKeys.size > 0) {
      setError(t("bulkEntry.invalidNumber"));
      return;
    }
    if (changes.length === 0) {
      onClose();
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const res = await saveBulkEntry(scorecardId, dimensionId, changes);
      toast.success(
        t("bulkEntry.saved", { updated: res.updated, cleared: res.cleared }),
      );
      onSaved();
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(
        detail?.code === "bsc_indicator_not_in_scorecard"
          ? t("bulkEntry.indicatorsMissing")
          : t("bulkEntry.saveFailed"),
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmDiscard = (body: string, onOk: () => void) => {
    Modal.confirm({
      title: t("bulkEntry.discardTitle"),
      content: body,
      okText: t("bulkEntry.discard"),
      okButtonProps: { danger: true },
      onOk,
    });
  };

  const handleCancel = () => {
    if (changes.length === 0) {
      onClose();
      return;
    }
    confirmDiscard(t("bulkEntry.discardBody", { count: changes.length }), onClose);
  };

  return (
    <Modal
      open={open}
      title={t("bulkEntry.title")}
      onCancel={handleCancel}
      width={960}
      footer={null}
      destroyOnClose
      className="bsc-bulk-modal"
    >
      <p className="bsc-bulk-subtitle">{t("bulkEntry.subtitle")}</p>

      <div className="bsc-bulk-dim-row">
        <span className="bsc-bulk-dim-label">{t("bulkEntry.dimension")}</span>
        <Select
          className="bsc-bulk-dim-select"
          value={dimensionId ?? undefined}
          onChange={(id: string) => {
            if (id === dimensionId) return;
            if (changes.length === 0) {
              setDimensionId(id);
              return;
            }
            confirmDiscard(
              t("bulkEntry.discardBodyDimension", {
                count: changes.length,
                dimension: dimension?.name ?? "",
              }),
              () => setDimensionId(id),
            );
          }}
          options={dimensions.map((d) => ({ label: d.name, value: d.id }))}
          disabled={saving}
        />
      </div>

      {loading || !grid ? (
        <div className="bsc-bulk-loading">
          <Spin />
        </div>
      ) : categories.length === 0 ? (
        <div className="bsc-bulk-empty">
          {t("bulkEntry.noCategories", { dimension: dimension?.name ?? "" })}
        </div>
      ) : !hasIndicators ? (
        <div className="bsc-bulk-empty">{t("bulkEntry.noIndicators")}</div>
      ) : (
        <div className="bsc-bulk-grid-wrap">
          <table className="bsc-bulk-table">
            <thead>
              <tr>
                <th className="bsc-bulk-th bsc-bulk-th--indicator">
                  {t("bulkEntry.indicator")}
                </th>
                {categories.map((c) => (
                  <th key={c.id} className="bsc-bulk-th">
                    {c.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.domains.map((domain) => (
                <React.Fragment key={domain.id}>
                  <tr className="bsc-bulk-domain-row">
                    <td colSpan={categories.length + 1}>{domain.name}</td>
                  </tr>
                  {domain.indicators.map((ind) => (
                    <tr key={ind.id} className="bsc-bulk-row">
                      <td className="bsc-bulk-indicator">
                        {ind.name}
                        {!ind.tagged && (
                          <span className="bsc-bulk-untagged">
                            {t("bulkEntry.notTagged", {
                              dimension: dimension?.name ?? "",
                            })}
                          </span>
                        )}
                      </td>
                      {categories.map((c) => {
                        const key = cellKey(ind.id, c.id);
                        const invalid = invalidKeys.has(key);
                        return (
                          <td key={c.id} className="bsc-bulk-td">
                            <input
                              className={`bsc-bulk-input ${invalid ? "is-invalid" : ""}`}
                              value={draft[key] ?? ""}
                              placeholder="—"
                              inputMode="decimal"
                              disabled={!canManage || saving}
                              onChange={(e) =>
                                setDraft((prev) => ({
                                  ...prev,
                                  [key]: e.target.value,
                                }))
                              }
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {outOfRange > 0 && (
        <div className="bsc-bulk-warning">
          {t("bulkEntry.outOfRange", { count: outOfRange })}
        </div>
      )}
      {error && <div className="bsc-dim-error">{error}</div>}

      <div className="bsc-bulk-footer">
        <span className="bsc-bulk-changes">
          {changes.length > 0
            ? t("bulkEntry.changed", { count: changes.length })
            : t("bulkEntry.noChanges")}
        </span>
        <div className="bsc-bulk-footer-actions">
          <button type="button" className="bsc-btn-cancel" onClick={handleCancel}>
            {t("bulkEntry.cancel")}
          </button>
          <button
            type="button"
            className="bsc-dim-save"
            onClick={handleSave}
            disabled={!canManage || saving || loading || !grid}
          >
            {saving && <Spin size="small" />} {t("bulkEntry.save")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkEntryModal;
